import { forwardRef } from 'react';
import type { KeyboardEvent, Ref } from 'react';

import { window } from '../../../globals';
import { onEnter } from '../../../utils';
import LabeledTextInput from '../../generic/Forms/LabeledTextInput';
import type { AuthContextState, AuthInput } from '../context/AuthContext';

type AuthFormEmailInputProps = Pick<AuthContextState, 'hidePasswordInput' | 'step'> & {
  email: Omit<AuthInput, 'ref'>;
  hasInvite: boolean;
  onSubmit: () => void;
};

/**
 * Email/username input for auth 2.0.
 */
const AuthFormEmailInput = ({
  email,
  hasInvite,
  hidePasswordInput,
  step,
  onSubmit,
}: AuthFormEmailInputProps, ref: Ref<HTMLInputElement>) => {
  //  LDAP uses usernames rather than email addresses, but since LDAP can
  //  ...only be enabled for on_prem, we only mention "usernames" in on-prem environments.
  const ldapSuffix = window.on_prem
    ? ' or username'
    : '';
  //  Disabled input if the user has already entered their email & we have disabled
  //  the password input (meaning the user will be using an SSO method to login).
  const isDisabled = (step === 'authenticate' && hidePasswordInput) || hasInvite;

  //  By default, <form>s will be submitted when a user hits "Enter" when a text field is in focus.
  //  While this is normally correct behavior, we don't want this to happen while on the first "step".
  //  Instead, we want it to "click" the Next button for us.
  const onKeyDown = onEnter((event: KeyboardEvent<HTMLInputElement>) => {
    if (step === 'enterEmail') {
      event.preventDefault();
      onSubmit();
    }
  });

  return (
    <LabeledTextInput
      labelVariant="legacy"
      ref={ref}
      type="email"
      label={`Email${ldapSuffix}`}
      placeholder={`Enter your email${ldapSuffix}`}
      onChange={email?.onChange}
      value={email?.value}
      isDisabled={isDisabled}
      onKeyDown={onKeyDown}
      size="large"
      testId="auth-email-input"
      isRequired
    />
  );
};

export default forwardRef<HTMLInputElement, AuthFormEmailInputProps>(AuthFormEmailInput);
